import { useTranslation } from 'react-i18next';
import { useEffect, useReducer } from 'react';
import configData from '../../config.json';
import { authenticate } from '../../services/api/authApi';
import {
  fetchWatchData,
  generateAssociationCode,
} from '../../services/api/watchDataApi';
import { fetchTranslations } from '../../services/api/translationsApi';
import {
  clearAuthParams,
  clearCardTypeParam,
  clearSensitiveParams,
  getAuthParams,
  hasAnyAuthParams,
} from '../../utils/app/queryParams';
import { isDev } from '../../utils/helpers/constants.helpers';
import {
  APP_PHASES,
  INITIALIZATION_ACTIONS,
  initialState,
  reducer,
} from './reducer';
import {
  createFailurePayload,
  isAbortError,
  normalizeGeneratedCode,
} from './utils';
import { trackAppEvent } from '../../analytics/appInsights';

const TOKEN_STORAGE_KEY = 'bearer';
const SERVICE_CARD_TYPE = 'sc';

const EXTERNAL_CARD_TYPE_REDIRECTS = Object.freeze({
  gc: 'https://www.rolex.com',
  cc: 'https://www.rolex.com/buying-a-rolex/rolex-certified-pre-owned',
  autht: 'https://www.rolex.com',
});

function normalizeCardType(cardType) {
  return typeof cardType === 'string' ? cardType.trim().toLowerCase() : '';
}

function normalizeStoredLanguage(i18n) {
  const storedLanguage = localStorage.getItem('i18nextLng');

  if (!storedLanguage) return;

  let normalizedLanguage = null;

  switch (storedLanguage.toLowerCase()) {
    case 'zh-tw':
    case 'zh-hk':
      normalizedLanguage = 'zh-Hant';
      break;

    case 'zh-cn':
    case 'zh':
      normalizedLanguage = 'zh-Hans';
      break;

    case 'en-ca':
      normalizedLanguage = 'en-US';
      break;

    default:
      break;
  }

  if (!normalizedLanguage) return;

  localStorage.setItem('i18nextLng', normalizedLanguage);
  i18n.changeLanguage(normalizedLanguage);
}

function getStoredToken() {
  const storedToken = sessionStorage.getItem(TOKEN_STORAGE_KEY);

  if (typeof storedToken !== 'string') return null;

  const normalizedToken = storedToken.trim();
  return normalizedToken || null;
}

function preserveLegacyCpoBehavior(watchData) {
  if (!watchData?.IsCpo) return watchData;

  // This preserves the current Rolex behavior from DataFetch, which disables
  // the CPO rendering branch. Reassess separately once the intended CPO
  // workflow has been confirmed.
  return {
    ...watchData,
    IsCpo: false,
  };
}

export function useAppInitialization() {
  const { i18n } = useTranslation();
  const [state, dispatch] = useReducer(reducer, initialState);

  useEffect(() => {
    const controller = new AbortController();
    let isActive = true;

    const dispatchIfActive = (action) => {
      if (isActive) {
        dispatch(action);
      }
    };

    const fail = (error) => {
      if (isAbortError(error) || !isActive) return;

      dispatch({
        type: INITIALIZATION_ACTIONS.FAIL,
        payload: createFailurePayload(error),
      });
    };

    const redirect = (destination, cardType = '') => {
      dispatchIfActive({
        type: INITIALIZATION_ACTIONS.REDIRECT,
        payload: { cardType },
      });

      window.location.replace(destination);
    };

    async function resolveToken(authParams) {
      const storedToken = getStoredToken();

      if (storedToken) {
        // Credentials are no longer needed once a session token already exists.
        // `type` remains until its workflow decision has been completed.
        clearAuthParams();
        return storedToken;
      }

      if (isDev) {
        clearAuthParams();
        return 'dev';
      }

      if (!hasAnyAuthParams(authParams)) {
        redirect(configData.redirectUrl, authParams.cardType);
        return null;
      }

      const authResult = await authenticate(authParams, {
        signal: controller.signal,
      });

      if (!isActive) return null;

      sessionStorage.setItem(TOKEN_STORAGE_KEY, authResult.token);

      // Preserve `type` until external redirects and service-card behavior have
      // been evaluated.
      clearAuthParams();

      return authResult.token;
    }

    async function loadProduct(token, cardType) {
      const watchResult = await fetchWatchData(token, {
        signal: controller.signal,
      });

      if (!isActive) return;

      clearCardTypeParam();

      const watchData = preserveLegacyCpoBehavior(watchResult.data);
      const rmc =
        watchData?.Configuration?.Rmc || watchData?.Configuration?.Model;

      let businessData = null;

      try {
        const translationsResult = await fetchTranslations(token, rmc, {
          signal: controller.signal,
        });

        if (!isActive) return;

        businessData = translationsResult.data;
      } catch (translationError) {
        if (isAbortError(translationError) || !isActive) return;

        // Preserve the current Rolex behavior: translation failure does not
        // replace the product result with the global error page.
        console.error('Translations request failed', translationError);
      }

      dispatchIfActive({
        type: INITIALIZATION_ACTIONS.PRODUCT_READY,
        payload: {
          watchData,
          businessData,
          status: watchResult.status,
          correlationId: watchResult.correlationId,
        },
      });
    }

    async function loadAssociation(token) {
      const associationResult = await generateAssociationCode(token, {
        signal: controller.signal,
      });

      if (!isActive) return;

      dispatchIfActive({
        type: INITIALIZATION_ACTIONS.ASSOCIATION_READY,
        payload: {
          generatedCode: normalizeGeneratedCode(associationResult.data),
          status: associationResult.status,
          correlationId: associationResult.correlationId,
        },
      });
    }

    async function run() {
      normalizeStoredLanguage(i18n);

      const authParams = getAuthParams();
      const cardType = normalizeCardType(authParams.cardType);

      dispatchIfActive({
        type: INITIALIZATION_ACTIONS.START,
        payload: { cardType },
      });

      try {
        const token = await resolveToken(authParams);

        if (!token || !isActive) return;

        const externalRedirect = EXTERNAL_CARD_TYPE_REDIRECTS[cardType];

        if (externalRedirect) {
          clearSensitiveParams();
          redirect(externalRedirect, cardType);
          return;
        }

        try {
          await loadProduct(token, cardType);
        } catch (watchError) {
          if (isAbortError(watchError) || !isActive) return;

          clearCardTypeParam();

          const shouldGenerateAssociationCode =
            watchError?.status === 404 && cardType === SERVICE_CARD_TYPE;

          if (!shouldGenerateAssociationCode) {
            fail(watchError);
            return;
          }

          try {
            await loadAssociation(token);
          } catch (associationError) {
            fail(associationError);
          }
        }
      } catch (authenticationError) {
        if (isAbortError(authenticationError) || !isActive) return;

        clearSensitiveParams();
        fail(authenticationError);
      }
    }

    trackAppEvent('Fetch', {});
    run();

    return () => {
      isActive = false;
      controller.abort();
    };
  }, [i18n]);

  return {
    ...state,
    loading:
      state.phase === APP_PHASES.INITIALIZING ||
      state.phase === APP_PHASES.REDIRECTING,
    error: state.phase === APP_PHASES.ERROR,
    association: state.phase === APP_PHASES.ASSOCIATION,
  };
}
